import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Users, Waves, ChevronRight, Sparkles, Star, Check, Clock, DumbbellIcon, Heart } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Contact from '../components/Contact';
import MembershipTiers from '../components/MembershipTiers';
import ComparisonTable from '../components/ComparisonTable';

const trialPasses = [
  {
    name: '24 HOUR PASS',
    price: '25',
    period: 'for 24 hours',
    description: 'Perfect for visitors',
    features: [
      'Full gym access',
      'Group fitness classes',
      'Wellness facilities',
      'Sauna & steam room',
      'Luxury changing rooms'
    ],
    color: 'from-blue-500/20 to-cyan-500/20',
    link: 'https://thefitnessportal.gymmasteronline.com/portal/membership/e0abaf905df0466c8b8e63cdf1e49e14'
  }
];

export default function MembershipPage() {
  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1534438327276-14e5300c3a48?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80')] bg-cover bg-center opacity-20" />
            <div className="absolute inset-0 bg-gradient-to-b from-black via-black/50 to-black" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-7xl font-bold mb-4">MEMBERSHIPS</h1>
              <p className="text-xl text-gray-400 mb-8">Choose your perfect membership plan</p>

              <div className="flex flex-wrap justify-center gap-4">
                <a
                  href="#memberships"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-8 py-4 rounded-full font-bold hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 group"
                >
                  <span>VIEW MEMBERSHIPS</span>
                  <ChevronRight className="w-5 h-5 transform group-hover:translate-x-1 transition-transform duration-300" />
                </a>
                <a
                  href="#contact"
                  className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-8 py-4 rounded-full font-bold hover:bg-white/20 transition-all duration-300"
                >
                  <span>CONTACT US</span>
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Premium Memberships */}
        <section id="memberships" className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl sm:text-7xl font-bold mb-4">PREMIUM MEMBERSHIPS</h2>
              <p className="text-xl text-gray-400">Choose your perfect membership plan</p>
            </motion.div>

            <MembershipTiers />
          </div>
        </section>

        {/* Day Pass Section */}
        <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl sm:text-7xl font-bold mb-4">DAY PASSES</h2>
              <p className="text-xl text-gray-400">Experience our world-class facilities</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-1 gap-8 max-w-2xl mx-auto">
              {trialPasses.map((pass, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1, duration: 0.8 }}
                  className="relative group"
                >
                  <div className="relative bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 p-8">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className={`bg-gradient-to-br ${pass.color} p-3 rounded-full`}>
                        <Calendar className="w-6 h-6 text-blue-400" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold">{pass.name}</h3>
                        <p className="text-gray-400">{pass.description}</p>
                      </div>
                    </div>

                    <div className="mb-8">
                      <div className="flex items-baseline">
                        <span className="text-5xl font-bold">${pass.price}</span>
                        <span className="text-gray-400 ml-2">{pass.period}</span>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-8">
                      {pass.features.map((feature, i) => (
                        <li key={i} className="flex items-center space-x-3">
                          <div className="w-1.5 h-1.5 bg-blue-400 rounded-full" />
                          <span className="text-gray-300">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <a
                      href={pass.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-center py-4 rounded-full font-bold transition-all duration-300 bg-gradient-to-r from-blue-600 to-cyan-600 text-white hover:from-blue-700 hover:to-cyan-700"
                    >
                      GET STARTED
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Membership Comparison Section */}
        <section className="py-20 bg-gradient-to-b from-black to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-5xl font-bold mb-4">MEMBERSHIP COMPARISON</h2>
              <p className="text-xl text-gray-400">Compare our membership options</p>
            </motion.div>

            <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl p-8 border border-blue-500/10">
              <ComparisonTable />
            </div>
          </div>
        </section>

        <Contact />
      </main>

      <Footer />
    </div>
  );
}